import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Share, SquarePlus, MoreVertical, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3">
    <span className="w-7 h-7 rounded-full bg-[#1877f2] text-white text-sm font-bold grid place-items-center shrink-0">{n}</span>
    <span className="text-sm text-[#050505] pt-1">{children} {Icon && <Icon className="inline w-4 h-4 text-[#1877f2] -mt-0.5" />}</span>
  </li>
);

function GuideDialog({ open, onOpenChange, ios }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="install-guide-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Smartphone className="w-5 h-5 text-[#1877f2]" /> Installer BuzzlyMe</DialogTitle>
          <DialogDescription>{ios ? "Sur iPhone et iPad, l'installation se fait depuis Safari en 3 gestes :" : "Depuis votre navigateur :"}</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 mt-2" data-testid={ios ? "install-guide-ios" : "install-guide-other"}>
          {ios ? (<>
            <Step n={1} icon={Share}>Touchez le bouton <b>Partager</b> en bas de Safari</Step>
            <Step n={2} icon={SquarePlus}>Choisissez <b>« Sur l'écran d'accueil »</b></Step>
            <Step n={3}>Touchez <b>Ajouter</b> : l'icône BuzzlyMe apparaît sur votre écran</Step>
          </>) : (<>
            <Step n={1} icon={MoreVertical}>Ouvrez le <b>menu</b> du navigateur, en haut à droite</Step>
            <Step n={2}>Choisissez <b>« Installer l'application »</b> ou <b>« Ajouter à l'écran d'accueil »</b></Step>
            <Step n={3}>Confirmez : BuzzlyMe s'ouvre comme une vraie app</Step>
          </>)}
        </ol>
        {ios && <p className="text-xs text-[#65676b] mt-3">Astuce : ouvrez buzzlyme.com dans Safari (pas dans Facebook ou Instagram) pour voir l'option.</p>}
      </DialogContent>
    </Dialog>
  );
}

export const InstallAppButton = ({ className = "btn-sharp", testId = "install-app-btn" }) => {
  const [prompt, setPrompt] = useState(() => window.__bmInstallPrompt || null);
  const [installed, setInstalled] = useState(isStandalone);
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    const ready = () => setPrompt(window.__bmInstallPrompt);
    const done = () => { setInstalled(true); window.__bmInstallPrompt = null; toast.success("BuzzlyMe est installée sur votre appareil"); };
    window.addEventListener("bm-install-ready", ready);
    window.addEventListener("appinstalled", done);
    return () => { window.removeEventListener("bm-install-ready", ready); window.removeEventListener("appinstalled", done); };
  }, []);

  if (installed) return null;

  const install = async () => {
    if (!prompt) return setGuide(true);
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    window.__bmInstallPrompt = null;
    setPrompt(null);
    if (outcome !== "accepted") toast.message("Installation annulée — vous pourrez réessayer à tout moment.");
  };

  return (
    <>
      <button type="button" onClick={install} className={className} data-testid={testId}>
        <Download className="w-4 h-4" /> Installer l'application
      </button>
      <GuideDialog open={guide} onOpenChange={setGuide} ios={isIOS()} />
    </>
  );
};
